/* eslint-disable max-classes-per-file */

/* iceberg-ui */
import { PieChart } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Check, FiberManualRecord } from '@mui/icons-material';
import { Box, TableCell, TableRow, Typography } from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import {
    LifecyclePicker,
    RiskPicker
} from 'application/Shared/Components/Pickers';
import { popoverContentsColumns } from 'application/Shared/Components/Recharts/PopoverContents';
import { SSOSupportIcons } from 'application/Shared/Components/SSOSupportIcons';
import { ManagedTable } from 'application/Shared/Components/Table';
import { TagLinksList } from 'application/Shared/Components/Tag';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers, SSOUtils } from 'application/Shared/Helpers';

/*  Objects  */
import { Application, ApplicationBusinessOwner } from 'objects/Application';
import { User, UserFromContext } from 'objects/User';

/*  React  */
import React, { Component, useMemo } from 'react';
import { withTranslation } from 'react-i18next';

/*  Constants  */
const reports = {
    expandUserDetails: 'discoveryDiscoveryexpandUserDetails'
};

const DiscoveryReports = (props) => {
    const { reportData, reportSetup, renderingScheduledReport } = props;

    const {
        discoveryDiscoveryOverview,
        discoveryDiscoveryRiskAnalysis,
        discoveryDiscoveryOverviewBreakdown,
        discoveryDiscoveryApplicationBreakdown
    } = reportData || {};

    return (
        <Box data-trackid="discoveryReport">
            {discoveryDiscoveryOverview && (
                <Box
                    data-trackid="discoveryReportDiv"
                    width="100%"
                    id="discoveryDiscoveryOverview"
                >
                    <DiscoveryOverview
                        {...props}
                        reportSetup={reportSetup}
                        reportData={discoveryDiscoveryOverview}
                    />
                </Box>
            )}
            {discoveryDiscoveryRiskAnalysis && (
                <Box id="discoveryDiscoveryRiskAnalysis" width="100%">
                    <DiscoveryRiskAnalysis
                        {...props}
                        reportSetup={reportSetup}
                        reportData={discoveryDiscoveryRiskAnalysis}
                    />
                </Box>
            )}
            {discoveryDiscoveryOverviewBreakdown && (
                <Box id="discoveryDiscoveryOverviewBreakdown" width="100%">
                    <DiscoveryOverviewBreakdown
                        {...props}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={discoveryDiscoveryOverviewBreakdown}
                    />
                </Box>
            )}
            {discoveryDiscoveryApplicationBreakdown && (
                <Box id="discoveryDiscoveryApplicationBreakdown" width="100%">
                    <DiscoveryApplicationBreakdown
                        {...props}
                        reportSetup={reportSetup}
                        reportData={discoveryDiscoveryApplicationBreakdown}
                    />
                </Box>
            )}
        </Box>
    );
};

class DiscoveryOverview extends Component {
    constructor(props) {
        super(props);
        this.state = { csvFields: [], data: [], loaded: false };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        const { t, reportData } = this.props;
        const { userApplicationActivityMap, discoveryOverviewMatrixTags } =
            reportData;

        const data = reportData.data.map((r) => {
            const row = r;
            const {
                applicationId,
                businessOwnerPlatformUserId,
                noBusinessOwnerRequired,
                status,
                purpose,
                isTagged
            } = row;
            if (
                !Helpers.isset(
                    userApplicationActivityMap,
                    applicationId,
                    'cloudLogin'
                )
            ) {
                return null;
            }

            const application = userApplicationActivityMap[applicationId];
            const {
                azureSignInActivity,
                cloudActivity,
                cloudLogin,
                desktopActivity,
                googleSignInActivity,
                googleSSO,
                microsoftSSO
            } = application;

            let hasBusinessOwner = false;
            if (noBusinessOwnerRequired || !!businessOwnerPlatformUserId) {
                hasBusinessOwner = true;
            }

            let hasStatus = false;
            if (!!status && status !== 'not_configured') {
                hasStatus = true;
            }

            let hasPurpose = false;
            if (purpose) {
                hasPurpose = true;
            }

            discoveryOverviewMatrixTags.map((tag) => {
                row[`tag_${tag.id}`] = isTagged[tag.id];
                return true;
            });

            return {
                ...row,
                azureSignInActivity,
                cloudActivity,
                cloudLogin,
                desktopActivity,
                googleSignInActivity,
                googleSSO,
                hasBusinessOwner,
                hasPurpose,
                hasStatus,
                microsoftSSO,
                ssoSupport: SSOUtils.tenantAppGetSSOSupport(row)
            };
        });

        const csvFields = [
            { key: 'application.name', label: t('Common.application') },
            { key: 'application.risk', label: t('Common.risk') },
            { key: 'userCount', label: t('Common.unique_users') },
            { key: 'cloudActivity', label: t('Common.cloud_users') },
            { key: 'cloudLogin', label: t('Common.cloud_logins') },
            { key: 'desktopActivity', label: t('Common.desktop_users') },
            {
                key: 'googleSSO',
                label: t('Common.google_sso')
            },
            {
                key: 'microsoftSSO',
                label: t('Common.microsoft_sso')
            },
            {
                key: 'azureSignInActivity',
                label: t('Common.microsoftEntraID.logins')
            },
            {
                key: 'googleSignInActivity',
                label: t('Common.google_logins')
            },
            { key: 'hasBusinessOwner', label: t('Common.business_owner') },
            { key: 'hasStatus', label: t('Common.lifecycleStage') },
            { key: 'hasPurpose', label: t('Common.purpose') }
        ];

        discoveryOverviewMatrixTags.forEach((tag) => {
            csvFields.push({
                key: `tag_${tag.id}`,
                label: tag.name
            });
        });

        this.setState({
            csvFields,
            data: data.filter((el) => el != null),
            loaded: true
        });
    };

    renderHeaders = () => {
        const { t, reportData } = this.props;
        const { discoveryOverviewMatrixTags } = reportData;

        const headers = [
            {
                align: 'left',
                field: 'riubApplication',
                renderHeader: <strong>{t('Common.application')}</strong>,
                width: '25%'
            },
            {
                align: 'center',
                field: 'overviewRisk',
                paddingLeft: '4px',
                paddingRight: '4px',
                renderHeader: <strong>{t('Common.risk')}</strong>,
                width: '15%'
            },
            {
                align: 'center',
                field: 'ssoCoverage',
                paddingLeft: '4px',
                paddingRight: '4px',
                renderHeader: <strong>{t('Common.ssoCoverage')}</strong>,
                width: '11%'
            },
            {
                align: 'center',
                field: 'userCount',
                paddingLeft: '4px',
                paddingRight: '4px',
                renderHeader: <strong>{t('Common.unique_users')}</strong>,
                width: '7%'
            },
            {
                align: 'center',
                field: 'cloudActivity',
                paddingLeft: '4px',
                paddingRight: '4px',
                renderHeader: <strong>{t('Common.cloud_users')}</strong>,
                width: '7%'
            },
            {
                align: 'center',
                field: 'cloudLogin',
                paddingLeft: '4px',
                paddingRight: '4px',
                renderHeader: <strong>{t('Common.cloud_logins')}</strong>,
                width: '7%'
            },
            {
                align: 'center',
                field: 'desktopActivity',
                paddingLeft: '4px',
                paddingRight: '4px',
                renderHeader: <strong>{t('Common.desktop_users')}</strong>,
                width: '7%'
            },
            {
                align: 'center',
                field: 'overviewBusinessOwner',
                paddingLeft: '4px',
                paddingRight: '4px',
                renderHeader: <strong>{t('Common.owner')}</strong>,
                width: '7%'
            },
            {
                align: 'center',
                field: 'overviewStatus',
                paddingLeft: '4px',
                paddingRight: '4px',
                renderHeader: <strong>{t('Common.lifecycle')}</strong>,
                width: '7%'
            },
            {
                align: 'center',
                field: 'overviewPurpose',
                paddingLeft: '4px',
                paddingRight: '4px',
                renderHeader: <strong>{t('Common.purpose')}</strong>,
                width: '7%'
            }
        ];

        discoveryOverviewMatrixTags.forEach((tag) => {
            headers.push({
                align: 'center',
                field: tag.id,
                paddingLeft: '4px',
                paddingRight: '4px',
                renderHeader: <strong>{tag.name}</strong>,
                width: '6%'
            });
        });

        return headers;
    };

    renderRow = (row) => {
        const { reportData } = this.props;
        const {
            applicationId,
            cloudActivity,
            cloudLogin,
            desktopActivity,
            hasBusinessOwner,
            hasStatus,
            userCount
        } = row;
        const { discoveryOverviewMatrixTags } = reportData;

        const greenCell = {
            backgroundColor: 'table.successRowBackground',
            color: 'table.successRowText'
        };
        const redCell = {
            backgroundColor: 'table.dangerRowBackground',
            color: 'table.dangerRowText'
        };

        return (
            <TableRow
                sx={{
                    backgroundColor: 'table.rowBackground',
                    maxHeight: '53px',
                    overflow: 'hidden'
                }}
                key={`app_overview_${applicationId}`}
            >
                <Application data={row.application}>
                    <TableCell>
                        <ApplicationNameLinkCell
                            scrollFrom
                            nameNoTruncate
                            withCategory
                        />
                    </TableCell>
                    <TableCell align="center" style={{ paddingRight: 2 }}>
                        <RiskPicker visualOnly value={row.application.risk} />
                    </TableCell>
                    <TableCell align="center">
                        <SSOSupportIcons
                            ssoSupport={row.ssoSupport}
                            enableTooltip
                            tenantApp={row}
                        />
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={userCount ? greenCell : redCell}
                    >
                        {userCount || (
                            <FiberManualRecord
                                style={{ fontSize: FontSizes[0] }}
                            />
                        )}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={cloudActivity ? greenCell : redCell}
                    >
                        {cloudActivity || (
                            <FiberManualRecord
                                style={{ fontSize: FontSizes[0] }}
                            />
                        )}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={cloudLogin !== 0 ? greenCell : redCell}
                    >
                        {cloudLogin !== 0 ? (
                            cloudLogin
                        ) : (
                            <FiberManualRecord
                                style={{ fontSize: FontSizes[0] }}
                            />
                        )}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={desktopActivity ? greenCell : redCell}
                    >
                        {desktopActivity || (
                            <FiberManualRecord
                                style={{ fontSize: FontSizes[0] }}
                            />
                        )}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={hasBusinessOwner ? greenCell : redCell}
                    >
                        {hasBusinessOwner ? (
                            <Check />
                        ) : (
                            <FiberManualRecord
                                style={{ fontSize: FontSizes[0] }}
                            />
                        )}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={hasStatus ? greenCell : redCell}
                    >
                        {hasStatus ? (
                            <Check />
                        ) : (
                            <FiberManualRecord
                                style={{ fontSize: FontSizes[0] }}
                            />
                        )}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={row.purpose ? greenCell : redCell}
                    >
                        {row.purpose ? (
                            <Check />
                        ) : (
                            <FiberManualRecord
                                style={{ fontSize: FontSizes[0] }}
                            />
                        )}
                    </TableCell>
                    {discoveryOverviewMatrixTags.map((tag) => (
                        <TableCell
                            align="center"
                            sx={row.isTagged[tag.id] ? greenCell : redCell}
                            key={`tag_${tag.id}`}
                        >
                            {row.isTagged[tag.id] ? (
                                <Check />
                            ) : (
                                <FiberManualRecord
                                    style={{
                                        fontSize: FontSizes[0]
                                    }}
                                />
                            )}
                        </TableCell>
                    ))}
                </Application>
            </TableRow>
        );
    };

    renderTable = () => {
        const { loaded, data, csvFields } = this.state;

        if (!loaded) {
            return null;
        }

        const headers = this.renderHeaders();

        return (
            <ManagedTable
                className="squeezedTable"
                csvExport
                csvExportFields={csvFields}
                csvExportFileName="discovery_application_overview"
                headers={headers}
                manageRows
                renderBody={this.renderRow}
                renderBodyKey={null}
                renderFooter
                rows={data}
                rowsPerPage={-1}
                searchEnabled={false}
                sortDirection="DESC"
                sortFieldType="risk"
                sortKey="application.risk"
            />
        );
    };

    render() {
        const { t } = this.props;

        const defsArray = [
            {
                property: t('Common.cloud_users'),
                text: t('Discovery.descriptions.cloud_users')
            },
            {
                property: t('Common.cloud_logins'),
                text: t('Discovery.descriptions.cloud_logins')
            },
            {
                property: t('Common.desktop_users'),
                text: t('Discovery.descriptions.desktop_users')
            },
            {
                property: t('Common.sso'),
                text: t('Discovery.descriptions.sso')
            },
            {
                property: t('Common.microsoftEntraID.logins'),
                text: t('Discovery.descriptions.microsoftEntraID')
            },
            {
                property: t('Common.business_owner'),
                text: t('Discovery.descriptions.owner')
            },
            {
                property: t('Common.lifecycleStage'),
                text: t('Discovery.descriptions.lifecycle')
            },
            {
                property: t('Common.purpose'),
                text: t('Discovery.descriptions.purpose')
            }
        ];

        return (
            <Box>
                <Box>
                    <Typography variant="h1">
                        {t('Reports.discoveryReport.overview.title')}
                    </Typography>
                    <Box>
                        <Typography paragraph mt="16px">
                            {t('Reports.discoveryReport.overview.description')}
                        </Typography>

                        {defsArray.map((row) => (
                            <Box key={row.property}>
                                <Box as="span" style={{ fontWeight: 600 }}>
                                    {row.property}
                                </Box>
                                <Box as="span">{` ${row.text}`}</Box>
                            </Box>
                        ))}
                    </Box>
                    <hr />
                </Box>
                <Box>{this.renderTable()}</Box>
            </Box>
        );
    }
}

const headersDiscoveryRiskAnalysis = (t) => [
    {
        align: 'left',
        field: 'riubApplication',
        renderHeader: <strong>{t('Common.risk')}</strong>,
        width: '70%'
    },
    {
        align: 'left',
        field: 'overviewCategory',
        renderHeader: <strong>{t('Common.count')}</strong>,
        width: '30%'
    }
];

const DiscoveryRiskAnalysis = (props) => {
    const { t, reportData } = props;

    const defsArray = [
        {
            property: t('Risks.critical'),
            text: t('Risks.critical')
        },
        {
            property: t('Risks.high'),
            text: t('Risks.high')
        },
        {
            property: t('Risks.medium'),
            text: t('Risks.medium')
        },
        {
            property: t('Risks.low'),
            text: t('Risks.low')
        },
        {
            property: t('Risks.safe'),
            text: t('Risks.safe')
        }
    ];

    const rows = reportData.data;

    const tableHeadersDiscoveryRiskAnalysis = useMemo(
        () => headersDiscoveryRiskAnalysis(t),
        [t]
    );

    return (
        <Box marginY="32px">
            <Typography variant="h1">
                {t('Reports.discoveryReport.riskAnalysis.title')}
            </Typography>
            <Box>
                <Typography paragraph mt="16px">
                    {t('Reports.discoveryReport.riskAnalysis.description')}
                </Typography>
                {defsArray.map((row) => (
                    <Box key={row.property}>
                        <Box as="span" style={{ fontWeight: 600 }}>
                            {`${row.property}.`}
                        </Box>
                        <Box as="span">{` ${row.text}`}</Box>
                    </Box>
                ))}
            </Box>
            <hr />
            <ManagedTable
                headers={tableHeadersDiscoveryRiskAnalysis}
                renderBody={(row) => (
                    <>
                        <TableCell>
                            <RiskPicker visualOnly value={row.name} />
                        </TableCell>
                        <TableCell>{row.count}</TableCell>
                    </>
                )}
                renderBodyKey={null}
                renderFooter={false}
                rows={rows}
                rowsPerPage={-1}
                searchEnabled={false}
                sortDirection="DESC"
                sortFieldType="number"
                sortKey="usage"
            />
        </Box>
    );
};

const headersBusinessOwner = (t) => [
    {
        align: 'left',
        field: 'riubApplication',
        renderHeader: <strong>{t('Common.business_owner')}</strong>,
        width: '70%'
    },
    {
        align: 'left',
        field: 'overviewCategory',
        renderHeader: <strong>{t('Common.count')}</strong>,
        width: '30%'
    }
];

const headersLifecycleStage = (t) => [
    {
        align: 'left',
        field: 'riubApplication',
        renderHeader: <strong>{t('Common.lifecycleStage')}</strong>,
        width: '70%'
    },
    {
        align: 'left',
        field: 'overviewCategory',
        renderHeader: <strong>{t('Common.count')}</strong>,
        width: '30%'
    }
];

const headersTag = (t) => [
    {
        align: 'left',
        field: 'riubApplication',
        renderHeader: <strong>{t('Common.tag')}</strong>,
        width: '70%'
    },
    {
        align: 'left',
        field: 'overviewCategory',
        renderHeader: <strong>{t('Common.count')}</strong>,
        width: '30%'
    }
];

const DiscoveryOverviewBreakdown = (props) => {
    const renderLifecycleStageOverview = () => {
        const { t, reportData, renderingScheduledReport } = props;
        const { statusCount } = reportData;

        const statusAssignmentCounts = [];
        Object.entries(statusCount).forEach(([statusKey, value]) => {
            statusAssignmentCounts.push({
                name: t(`Common.${statusKey}`),
                value
            });
        });

        return (
            <Box>
                <Typography variant="h1" style={{ paddingTop: '32px' }}>
                    {t('Reports.discoveryReport.statusAssignment.overview')}
                </Typography>
                <Typography paragraph mt="16px">
                    {t('DiscoveryReport.LifecycleOverview.description')}
                </Typography>
                <PieChart
                    data={statusAssignmentCounts}
                    emptyMessage={t('Common.noData')}
                    isAnimationActive={!renderingScheduledReport}
                    popoverContentsColumns={popoverContentsColumns}
                    responsiveContainerHeight={250}
                />
            </Box>
        );
    };

    const { t, reportData, renderingScheduledReport } = props;
    const {
        discoveryOverviewMatrixTags,
        businessOwnerCount,
        total,
        statusCount,
        tagCount
    } = reportData;

    const tagsToRender = Object.keys(discoveryOverviewMatrixTags);

    const businessOwnerCounts = useMemo(
        () => [
            {
                name: t('Reports.discoveryReport.businessOwner.assigned'),
                value: businessOwnerCount
            },
            {
                name: t('Reports.discoveryReport.businessOwner.notAssigned'),
                value: total - businessOwnerCount
            }
        ],
        [businessOwnerCount, total, t]
    );

    const statusAssignmentCounts = Object.entries(statusCount).reduce(
        (acc, [statusKey, value]) => {
            acc.push({
                name: statusKey,
                value
            });
            return acc;
        },
        []
    );

    const rowsStatusAssignmentCounts = useMemo(
        () => statusAssignmentCounts,
        [statusAssignmentCounts]
    );

    const tableHeadersBusinessOwner = useMemo(
        () => headersBusinessOwner(t),
        [t]
    );

    const tableHeadersLifecycleStage = useMemo(
        () => headersLifecycleStage(t),
        [t]
    );

    const tableHeadersTag = useMemo(() => headersTag(t), [t]);

    return (
        <Box marginY="32px">
            <Box>
                <Typography variant="h1">
                    {t(
                        'Reports.discoveryReport.businessOwnerAssignmentOverview'
                    )}
                </Typography>
                <Typography paragraph mt="16px">
                    {t('Reports.discoveryReport.businessOwner.description')}
                </Typography>
                <Box>
                    <PieChart
                        data={businessOwnerCounts}
                        emptyMessage={t('Common.noData')}
                        isAnimationActive={!renderingScheduledReport}
                        popoverContentsColumns={popoverContentsColumns}
                        responsiveContainerHeight={250}
                    />
                </Box>
            </Box>
            <Box>
                <ManagedTable
                    headers={tableHeadersBusinessOwner}
                    renderBody={(row) => (
                        <>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{row.value}</TableCell>
                        </>
                    )}
                    renderBodyKey={null}
                    renderFooter={false}
                    rows={businessOwnerCounts}
                    rowsPerPage={5}
                    searchEnabled={false}
                    sortDirection="DESC"
                    sortFieldType="number"
                    sortKey="usage"
                />
            </Box>
            {renderLifecycleStageOverview()}
            <Box>
                <ManagedTable
                    headers={tableHeadersLifecycleStage}
                    renderBody={(row) => (
                        <>
                            <TableCell>{t(`Common.${row.name}`)}</TableCell>
                            <TableCell>{row.value}</TableCell>
                        </>
                    )}
                    renderBodyKey={null}
                    renderFooter={false}
                    rows={rowsStatusAssignmentCounts}
                    rowsPerPage={-1}
                    searchEnabled={false}
                    sortDirection="DESC"
                    sortFieldType="number"
                    sortKey="usage"
                />
            </Box>
            {tagsToRender.map((tagId) => {
                const tagCounts = [
                    {
                        name: discoveryOverviewMatrixTags[tagId].name,
                        value: tagCount[tagId]
                    },
                    {
                        name: t('Tags.noNameTag', {
                            name: discoveryOverviewMatrixTags[tagId].name
                        }),

                        value: total - tagCount[tagId]
                    }
                ];
                return (
                    <span key={tagId}>
                        <Box mt="32px" style={{ pageBreakBefore: 'always' }}>
                            <Typography variant="h1">
                                {`${
                                    discoveryOverviewMatrixTags[tagId].name
                                } ${t('Common.overview')}`}
                            </Typography>
                            <PieChart
                                data={tagCounts}
                                emptyMessage={t('Common.noData')}
                                isAnimationActive={!renderingScheduledReport}
                                popoverContentsColumns={popoverContentsColumns}
                                responsiveContainerHeight={250}
                            />
                        </Box>
                        <Box pt="16px">
                            <ManagedTable
                                headers={tableHeadersTag}
                                renderBody={(row) => (
                                    <>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.value}</TableCell>
                                    </>
                                )}
                                renderBodyKey={null}
                                renderFooter={false}
                                rows={tagCounts}
                                rowsPerPage={-1}
                                searchEnabled={false}
                                sortDirection="DESC"
                                sortFieldType="number"
                                sortKey="usage"
                            />
                        </Box>
                    </span>
                );
            })}
        </Box>
    );
};

const renderAppInfo = (data, t) => {
    const {
        businessOwnerPlatformUserId,
        noBusinessOwnerRequired,
        managedByPartner,
        managed3rdParty,
        tagLinks,
        status,
        ssoSupport
    } = data;

    return (
        <Box display="flex" mt="8px">
            <Box width="25%">
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.business_owner')}:
                </Typography>
                <Application data={data}>
                    <ApplicationBusinessOwner
                        businessOwnerPlatformUserId={
                            businessOwnerPlatformUserId
                        }
                        noBusinessOwnerRequired={noBusinessOwnerRequired}
                        managedByPartner={managedByPartner}
                        managed3rdParty={managed3rdParty}
                    />
                </Application>
            </Box>
            <Box width="25%" paddingRight="4px">
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.tags')}
                </Typography>
                <TagLinksList tagLinks={tagLinks} />
            </Box>
            <Box width="25%">
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.lifecycleStage')}:
                </Typography>
                <LifecyclePicker visualOnly value={status} />
            </Box>
            <Box width="25%">
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.ssoCoverageWithDivider')}
                </Typography>
                <SSOSupportIcons
                    ssoSupport={ssoSupport}
                    enableTooltip
                    tenantApp={data}
                    justify="left"
                />
            </Box>
        </Box>
    );
};

class DiscoveryApplicationBreakdown extends Component {
    constructor(props) {
        super(props);
        this.state = {
            applications: [],
            expandUserDetails: false
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        const { reportData, reportSetup } = this.props;
        const { data } = reportData;
        if (!data) {
            return;
        }

        const { reportSelections } = reportSetup;
        const expandUserDetails = reportSelections?.includes(
            reports.expandUserDetails
        );

        const applications = data.map((app) => ({
            ...app,
            ssoSupport: SSOUtils.tenantAppGetSSOSupport(app)
        }));

        this.setState({ applications, expandUserDetails });
    };

    renderHeader = () => {
        const { t } = this.props;

        return (
            <Box>
                <Typography variant="h1">
                    {t('Reports.discoveryReport.discoveryApplicationBreakdown')}
                </Typography>
                <Typography paragraph mt="16px">
                    {t(
                        'Reports.discoveryReport.applicationBreakdown.description'
                    )}
                </Typography>
                <hr />
            </Box>
        );
    };

    renderNotes = (data) => {
        const { t } = this.props;
        const { notes } = data;
        if (Helpers.isEmpty(notes)) {
            return null;
        }

        return (
            <Box pb="16px">
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.notes')}:
                </Typography>
                {notes.map((n) => {
                    const { id, type, message } = n;
                    return (
                        <Box key={`note_${id}`} mb="16px">
                            <Box as="span" style={{ fontWeight: 600 }}>
                                <small>{`${type} `}</small>
                            </Box>
                            <Box as="span">{message}</Box>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    renderUsers = (data) => {
        const { t, reportSetup } = this.props;
        const { application, userDetails } = data;
        const appId = application.id;

        const renderUserDetails = Array.isArray(userDetails)
            ? userDetails
            : [userDetails];

        return (
            <Box>
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.unique_users')}:
                </Typography>
                {renderUserDetails.length > 0 ? (
                    renderUserDetails.map((u) => (
                        <Box mb="4px" key={`app_${appId}_user_${u.id}`}>
                            <User
                                data={u}
                                clientId={reportSetup?.platformTenantId}
                            >
                                <UserFromContext link />
                            </User>
                        </Box>
                    ))
                ) : (
                    <Typography>{t('Common.noData')}</Typography>
                )}
            </Box>
        );
    };

    renderRow = (data) => {
        const { t } = this.props;
        const { expandUserDetails } = this.state;
        const { application, purpose } = data;

        return (
            <Application data={application} nameHasLink>
                <Box pt="4px">
                    <Typography
                        component="span"
                        style={{
                            textTransform: 'uppercase'
                        }}
                    >
                        <ApplicationNameLinkCell
                            categoryNoTruncate
                            nameNoTruncate
                            scrollTo
                            withCategory
                        />
                    </Typography>
                </Box>
                <Box pl="16px">
                    <Box>{renderAppInfo(data, t)}</Box>

                    {!!application.risk && (
                        <Box mt="8px">
                            <Typography style={{ fontWeight: 600 }}>
                                {t('Reports.discoveryReport.risk.label')}
                            </Typography>
                            <RiskPicker visualOnly value={application.risk} />
                        </Box>
                    )}
                    <Typography
                        style={{
                            fontWeight: 600,
                            marginTop: '8px'
                        }}
                    >
                        {t('Reports.discoveryReport.businessPurpose.label')}
                    </Typography>
                    {purpose ? (
                        <Typography mb="8px">{purpose}</Typography>
                    ) : (
                        <Typography mb="8px" color="text.danger">
                            {t('Reports.discoveryReport.noPurposeSet')}
                        </Typography>
                    )}
                    {this.renderNotes(data)}
                    {expandUserDetails && this.renderUsers(data)}
                </Box>
            </Application>
        );
    };

    render() {
        const { applications } = this.state;

        return (
            <Box paddingY="32px" width="100%">
                {this.renderHeader()}

                {applications.map((d, i) => (
                    <Box
                        key={`app_breakdown_tableRow_${d.id}`}
                        sx={{
                            '&:last-child td, &:last-child th': {
                                border: 0
                            }
                        }}
                        className="noBreak"
                        backgroundColor={
                            i % 2 === 0
                                ? 'table.evenRowBackground'
                                : 'table.oddRowBackground'
                        }
                        m="8px"
                    >
                        {this.renderRow(d)}
                    </Box>
                ))}
            </Box>
        );
    }
}

export default withTranslation()(DiscoveryReports);
